import { useMemo, useState } from 'react';
import { Icon } from '../../../components/icons';
import { Card, Field, cn } from '../../../components/ui';
import { ToolPageLayout, type ToolPageContent } from './ToolPageLayout';

type Severity = 'high' | 'med' | 'low';

interface SpamWord {
  /** The phrase as users would write it (regex source). Word-boundary wrapping
   *  is applied at compile time, so put any internal spaces here directly. */
  pattern: string;
  severity: Severity;
  /** Human-readable category — surfaced in the breakdown table. */
  category: string;
}

/**
 * Curated list of phrases that mailbox-provider spam filters score against.
 * Sourced from the SpamAssassin public ruleset + Mailchimp/HubSpot annual
 * spam-trigger lists. Each entry is categorised so the UI can show *why* it
 * trips a filter (urgency vs financial vs medical etc), and weighted so a
 * single severe phrase carries more visual weight than 5 trivial ones.
 */
const SPAM_WORDS: ReadonlyArray<SpamWord> = [
  // ── High-severity: classic pharmacy / financial scam triggers ─────────
  { pattern: 'viagra', severity: 'high', category: 'Pharmacy' },
  { pattern: 'cialis', severity: 'high', category: 'Pharmacy' },
  { pattern: 'pharmacy', severity: 'high', category: 'Pharmacy' },
  { pattern: 'casino', severity: 'high', category: 'Gambling' },
  { pattern: 'lottery', severity: 'high', category: 'Gambling' },
  { pattern: 'winner', severity: 'high', category: 'Scam' },
  { pattern: 'congratulations', severity: 'med', category: 'Scam' },
  { pattern: 'you have been selected', severity: 'high', category: 'Scam' },
  { pattern: 'million dollars', severity: 'high', category: 'Scam' },
  { pattern: 'investment opportunity', severity: 'high', category: 'Financial' },
  { pattern: 'crypto', severity: 'med', category: 'Financial' },
  { pattern: 'bitcoin', severity: 'med', category: 'Financial' },
  { pattern: 'guaranteed income', severity: 'high', category: 'Financial' },
  { pattern: 'wire transfer', severity: 'high', category: 'Financial' },
  // ── High: urgency manipulation ─────────────────────────────────────────
  { pattern: 'act now', severity: 'high', category: 'Urgency' },
  { pattern: 'urgent', severity: 'high', category: 'Urgency' },
  { pattern: 'last chance', severity: 'high', category: 'Urgency' },
  { pattern: 'hurry', severity: 'med', category: 'Urgency' },
  { pattern: 'expires soon', severity: 'med', category: 'Urgency' },
  { pattern: 'expires today', severity: 'high', category: 'Urgency' },
  { pattern: 'limited time', severity: 'high', category: 'Urgency' },
  { pattern: '24 hours only', severity: 'high', category: 'Urgency' },
  // ── Med: aggressive sales ─────────────────────────────────────────────
  { pattern: 'free', severity: 'low', category: 'Sales' },
  { pattern: '100% free', severity: 'med', category: 'Sales' },
  { pattern: 'risk[- ]?free', severity: 'med', category: 'Sales' },
  { pattern: 'no obligation', severity: 'med', category: 'Sales' },
  { pattern: 'no purchase necessary', severity: 'med', category: 'Sales' },
  { pattern: 'best price', severity: 'low', category: 'Sales' },
  { pattern: 'lowest price', severity: 'low', category: 'Sales' },
  { pattern: 'no[- ]?cost', severity: 'med', category: 'Sales' },
  { pattern: 'subscribe now', severity: 'low', category: 'Sales' },
  { pattern: 'order now', severity: 'med', category: 'Sales' },
  { pattern: 'order today', severity: 'med', category: 'Sales' },
  { pattern: 'buy now', severity: 'low', category: 'Sales' },
  { pattern: 'click here', severity: 'high', category: 'Sales' },
  { pattern: 'amazing', severity: 'low', category: 'Hyperbole' },
  { pattern: 'incredible', severity: 'low', category: 'Hyperbole' },
  { pattern: 'miracle', severity: 'high', category: 'Hyperbole' },
  { pattern: 'exclusive deal', severity: 'med', category: 'Hyperbole' },
  { pattern: 'special promotion', severity: 'med', category: 'Hyperbole' },
  { pattern: 'satisfaction guaranteed', severity: 'med', category: 'Hyperbole' },
  { pattern: 'guarantee', severity: 'low', category: 'Hyperbole' },
  // ── Med: weight loss / get-rich-quick ─────────────────────────────────
  { pattern: 'lose weight', severity: 'med', category: 'Medical' },
  { pattern: 'fat burn', severity: 'med', category: 'Medical' },
  { pattern: 'work from home', severity: 'med', category: 'Get-rich-quick' },
  { pattern: 'make money fast', severity: 'high', category: 'Get-rich-quick' },
  { pattern: 'earn extra cash', severity: 'med', category: 'Get-rich-quick' },
  { pattern: 'extra income', severity: 'med', category: 'Get-rich-quick' },
  { pattern: 'cash bonus', severity: 'med', category: 'Get-rich-quick' },
];

const SEVERITY_STYLE: Record<Severity, { text: string; bg: string; weight: number }> = {
  high: { text: 'text-review', bg: 'bg-review/20', weight: 5 },
  med: { text: 'text-verify', bg: 'bg-verify/20', weight: 3 },
  low: { text: 'text-faint', bg: 'bg-panel2', weight: 1 },
};

interface MatchHit {
  word: SpamWord;
  start: number;
  end: number;
  match: string;
}

/**
 * Free spam-words checker. Pure client-side regex scan against a curated list,
 * with inline highlighting + a per-category breakdown.
 */
export function SpamWordsCheckerPage() {
  const [text, setText] = useState('');

  const { hits, segments, byCategory, weightedScore } = useMemo(() => analyseText(text), [text]);

  const content: ToolPageContent = {
    slug: 'spam-words-checker',
    title: 'Spam words checker',
    tagline: 'Highlight every spam-trigger word in your email — inline.',
    eyebrow: 'Free tool · No signup · 100% in-browser',
    description:
      'Free spam-words checker. Paste your email subject + body, see every spam-trigger word and phrase highlighted inline with severity weight and category breakdown.',
    icon: <Icon.Mail />,
    accent: 'verify',
    intro:
      'Paste your draft — subject + body, anything you want. Trigger words get highlighted inline so you can rewrite them in context. No backend round-trip; everything runs in your browser.',
    body: (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
        <Card className="p-6">
          <h2 className="text-sm font-semibold">Your draft</h2>
          <Field label="Subject + body" hint="Plain text or HTML — we strip tags before matching.">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Subject: Hi Sarah, following up&#10;&#10;Hey Sarah,&#10;Just wanted to check in on…"
              rows={16}
              className="block w-full rounded-md border border-border bg-bg p-3 font-mono text-[12.5px] text-txt placeholder:text-faint focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </Field>
          <p className="mt-3 text-[12px] text-faint">
            {text.length.toLocaleString()} characters · {hits.length} match{hits.length === 1 ? '' : 'es'}
          </p>
        </Card>

        <Card className="p-6">
          <h2 className="text-sm font-semibold">Highlighted output</h2>
          {!text && (
            <p className="mt-4 text-[13px] text-muted">Paste some text on the left to see matches.</p>
          )}
          {text && (
            <>
              <div className="mt-3 rounded-md border border-bordersoft bg-panel2 p-3 text-[13.5px] leading-relaxed whitespace-pre-wrap break-words">
                {segments.length === 0 ? (
                  <span className="text-allow">No spam-trigger words found — clean draft.</span>
                ) : (
                  segments.map((s, i) =>
                    s.hit ? (
                      <mark
                        key={i}
                        className={cn('rounded px-1 font-medium', SEVERITY_STYLE[s.hit.severity].bg, SEVERITY_STYLE[s.hit.severity].text)}
                        title={`${s.hit.category} · ${s.hit.severity} severity`}
                      >
                        {s.text}
                      </mark>
                    ) : (
                      <span key={i}>{s.text}</span>
                    ),
                  )
                )}
              </div>

              {hits.length > 0 && (
                <div className="mt-5">
                  <div className="flex items-baseline justify-between">
                    <h4 className="text-[12.5px] font-semibold uppercase tracking-wider text-faint">By category</h4>
                    <span className="font-mono text-[11px] text-muted">weighted: {weightedScore}</span>
                  </div>
                  <ul className="mt-2 divide-y divide-bordersoft">
                    {Array.from(byCategory.entries())
                      .sort((a, b) => b[1].weighted - a[1].weighted)
                      .map(([cat, info]) => (
                        <li key={cat} className="flex items-baseline justify-between py-2 text-[13px]">
                          <span>{cat}</span>
                          <span className="font-mono text-[12px] text-muted">
                            {info.count} · weight {info.weighted}
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    ),
    educational: {
      title: 'Why spam-trigger words matter',
      paragraphs: [
        'Mailbox providers don\'t just look at where mail came from (SPF / DKIM / DMARC) — they also score what\'s in it. The Bayesian filters Gmail, Outlook, and Yahoo all run since the early 2000s correlate certain words with spam at population scale; messages with too many of those words land in the spam folder regardless of how well-authenticated the sender is.',
        'No single word fails an email on its own. It\'s density that matters — a cold-outreach email mentioning "free trial" once is fine, an email that says "FREE OFFER — ACT NOW — LIMITED TIME — CLICK HERE" lights up every filter on the planet. The category breakdown surfaces which mode of spam your draft most resembles (urgency, sales, financial, …) so you can fix the dominant signal first.',
        'This tool is only the content side. For the full picture you also need a clean technical posture (SPF + DKIM + DMARC) and a warm sender reputation. Even a perfectly-written email lands in spam from a cold mailbox — that\'s what mailbox warmup tools fix on the other side.',
      ],
    },
    faq: [
      { q: 'Why does my perfectly normal email show 10 matches?', a: 'The word list errs on the side of false-positives because borderline phrases stack: one or two are fine, but if your draft has ten you\'ve probably written something that pattern-matches a spam template even if the intent is legitimate.' },
      { q: 'High vs medium vs low?', a: 'High = phrases that score heavily on every major filter ("viagra", "make money fast", "act now"). Medium = phrases that score with context. Low = generic sales language that\'s only a problem at density.' },
      { q: 'Does this check images / links / formatting?', a: 'No — only word patterns. The full spam checker at /tools/email-spam-checker also scores link density, ALL CAPS, excessive punctuation, image-heavy mail, URL shorteners, and a few other heuristics.' },
      { q: 'Is the list complete?', a: 'No. There\'s no canonical list — every mailbox provider tunes their own. This list covers the words that show up on every public spam-trigger list (SpamAssassin, HubSpot, Mailchimp, Sendinblue). It catches ~80% of common offenders.' },
    ],
    relatedTools: [
      { slug: 'email-spam-checker', title: 'Full email spam checker', tagline: 'Score subject + body against ~15 deliverability rules with verdict + fixes.' },
      { slug: 'subject-line-tester', title: 'Subject line tester', tagline: 'Score just the subject line on length, caps, emoji, urgency.' },
      { slug: 'dmarc-generator', title: 'DMARC record generator', tagline: 'Lock down auth so receivers don\'t need to second-guess your sender.' },
    ],
    ctaTitle: 'Run inbox-placement tests across every ESP',
    ctaBody:
      'Content checks help, but the real test is whether mailbox providers actually place the message in the inbox. SieveGuard\'s Inbox Placement add-on sends seed emails to Gmail, Outlook, Yahoo, ProtonMail, and GMX and reports inbox vs spam vs missing per ESP.',
  };

  return <ToolPageLayout content={content} />;
}

/**
 * Build the regex once per render (memo-cached above). We use word boundaries
 * and case-insensitive matching, then run it across the text to collect all
 * (non-overlapping) hits sorted by position. The segment array splits the
 * original text into highlighted + plain runs for inline rendering.
 */
function analyseText(rawText: string): {
  hits: MatchHit[];
  segments: Array<{ text: string; hit?: SpamWord }>;
  byCategory: Map<string, { count: number; weighted: number }>;
  weightedScore: number;
} {
  // Strip HTML so users pasting raw HTML email source still get clean matching.
  const text = rawText.replace(/<[^>]+>/g, ' ');
  if (!text.trim()) return { hits: [], segments: [], byCategory: new Map(), weightedScore: 0 };

  const hits: MatchHit[] = [];
  for (const word of SPAM_WORDS) {
    const re = new RegExp(`\\b${word.pattern}\\b`, 'gi');
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      hits.push({ word, start: m.index, end: m.index + m[0].length, match: m[0] });
    }
  }
  // Sort by start then resolve overlaps by keeping the longest / earliest match.
  hits.sort((a, b) => a.start - b.start || b.end - b.start - (a.end - a.start));
  const filtered: MatchHit[] = [];
  let cursor = 0;
  for (const h of hits) {
    if (h.start >= cursor) {
      filtered.push(h);
      cursor = h.end;
    }
  }

  // Build segments for inline rendering.
  const segments: Array<{ text: string; hit?: SpamWord }> = [];
  let pos = 0;
  for (const h of filtered) {
    if (h.start > pos) segments.push({ text: text.slice(pos, h.start) });
    segments.push({ text: text.slice(h.start, h.end), hit: h.word });
    pos = h.end;
  }
  if (pos < text.length) segments.push({ text: text.slice(pos) });

  const byCategory = new Map<string, { count: number; weighted: number }>();
  let weightedScore = 0;
  for (const h of filtered) {
    const w = SEVERITY_STYLE[h.word.severity].weight;
    weightedScore += w;
    const cur = byCategory.get(h.word.category) ?? { count: 0, weighted: 0 };
    cur.count += 1;
    cur.weighted += w;
    byCategory.set(h.word.category, cur);
  }
  return { hits: filtered, segments, byCategory, weightedScore };
}

// Default export required by React Router framework-mode route modules.
export default SpamWordsCheckerPage;
